// src/pages/business/settings/BusinessHoursPage.tsx
// Web adaptation of mobile BusinessHoursScreen

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Clock, Save, Users, ChevronRight, Check, X,
  Loader2, AlertCircle, ToggleLeft
} from 'lucide-react';
import apiClient from '@/services/apiClient';

type DaySchedule = {
  isOpen: boolean;
  openTime: string; // HH:MM format
  closeTime: string; // HH:MM format
};

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const initialHours: Record<string, DaySchedule> = {
  Monday: { isOpen: true, openTime: '09:00', closeTime: '18:00' },
  Tuesday: { isOpen: true, openTime: '09:00', closeTime: '18:00' },
  Wednesday: { isOpen: true, openTime: '09:00', closeTime: '18:00' },
  Thursday: { isOpen: true, openTime: '09:00', closeTime: '18:00' },
  Friday: { isOpen: true, openTime: '09:00', closeTime: '18:00' },
  Saturday: { isOpen: true, openTime: '10:00', closeTime: '16:00' },
  Sunday: { isOpen: false, openTime: '10:00', closeTime: '16:00' },
};

export const BusinessHoursPage: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();

  const [schedule, setSchedule] = useState<Record<string, DaySchedule>>(initialHours);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  useEffect(() => {
    fetchBusinessHours();
  }, [businessId]);

  const fetchBusinessHours = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/businesses/${businessId}/hours`);
      if (data?.hours) {
        setSchedule(data.hours);
      }
    } catch (err: any) {
      console.error('Failed to fetch business hours:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleDay = (day: string) => {
    setSchedule((prev) => ({
      ...prev,
      [day]: { ...prev[day], isOpen: !prev[day].isOpen },
    }));
    setUnsavedChanges(true);
  };

  const updateTime = (day: string, type: 'openTime' | 'closeTime', value: string) => {
    setSchedule((prev) => ({
      ...prev,
      [day]: { ...prev[day], [type]: value },
    }));
    setUnsavedChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put(`/businesses/${businessId}/hours`, { hours: schedule });
      setUnsavedChanges(false);
      setShowSaveConfirm(true);
      setTimeout(() => setShowSaveConfirm(false), 3000);
    } catch (err: any) {
      console.error('Failed to save business hours:', err);
      alert('Failed to save business hours. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const formatTimeDisplay = (time: string) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getOperatingHoursSummary = () => {
    const openDays = DAYS.filter((day) => schedule[day].isOpen);
    if (openDays.length === 0) return 'Closed all days';
    if (openDays.length === 7) return 'Open daily';
    return `${openDays.length} days/week`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-light-bg dark:bg-dark-bg flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 size={40} className="animate-spin" style={{ color: '#4F46E5' }} />
          <p className="text-sm" style={{ color: '#64748B' }}>Loading business hours...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(`/business/${businessId}`)}
              className="p-2 rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
            >
              <ArrowLeft size={24} style={{ color: '#4F46E5' }} />
            </button>
            <div>
              <p className="font-bold text-lg" style={{ color: '#4F46E5' }}>Business Availability</p>
              <p className="text-xs" style={{ color: '#64748B' }}>{getOperatingHoursSummary()}</p>
            </div>
          </div>
          {unsavedChanges && (
            <span
              className="px-3 py-1 rounded-full text-xs font-semibold"
              style={{ backgroundColor: 'rgba(243,156,18,0.12)', color: '#f39c12' }}
            >
              Unsaved changes
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Main Schedule Card */}
        <div className="bg-light-card dark:bg-dark-card rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'rgba(79,70,229,0.12)' }}
            >
              <Clock size={20} style={{ color: '#4F46E5' }} />
            </div>
            <div>
              <h3 className="font-semibold" style={{ color: '#0F172A' }}>Weekly Operating Hours</h3>
              <p className="text-sm" style={{ color: '#64748B' }}>Set your business availability for each day</p>
            </div>
          </div>

          <div className="space-y-1">
            {DAYS.map((day) => {
              const { isOpen, openTime, closeTime } = schedule[day];

              return (
                <div
                  key={day}
                  className="flex items-center justify-between py-4 border-b border-light-border dark:border-dark-border last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`font-medium ${!isOpen ? 'opacity-40' : ''}`}
                      style={{ color: '#0F172A' }}
                    >
                      {day}
                    </span>
                  </div>

                  <div className="flex items-center gap-4">
                    {/* Toggle Switch */}
                    <button
                      onClick={() => toggleDay(day)}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        isOpen ? '' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                      style={{ backgroundColor: isOpen ? '#d1a02f' : undefined }}
                    >
                      <div
                        className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${
                          isOpen ? 'translate-x-7' : 'translate-x-1'
                        }`}
                      />
                    </button>

                    {/* Time Pickers */}
                    {isOpen ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="time"
                          value={openTime}
                          onChange={(e) => updateTime(day, 'openTime', e.target.value)}
                          className="px-3 py-1.5 rounded-lg text-sm border focus:outline-none focus:ring-2 transition-all"
                          style={{
                            borderColor: '#E2E8F0',
                            color: '#0F172A',
                            backgroundColor: 'transparent',
                          }}
                        />
                        <span style={{ color: '#64748B' }}>—</span>
                        <input
                          type="time"
                          value={closeTime}
                          onChange={(e) => updateTime(day, 'closeTime', e.target.value)}
                          className="px-3 py-1.5 rounded-lg text-sm border focus:outline-none focus:ring-2 transition-all"
                          style={{
                            borderColor: '#E2E8F0',
                            color: '#0F172A',
                            backgroundColor: 'transparent',
                          }}
                        />
                      </div>
                    ) : (
                      <span className="text-sm italic" style={{ color: '#94A3B8' }}>
                        Closed
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-light-card dark:bg-dark-card rounded-xl p-5 shadow-sm">
          <h3 className="font-semibold mb-4" style={{ color: '#0F172A' }}>Quick Actions</h3>
          <div className="space-y-2">
            <button
              onClick={() => {
                const newSchedule = { ...schedule };
                DAYS.forEach((day) => {
                  newSchedule[day] = { ...newSchedule[day], isOpen: true };
                });
                setSchedule(newSchedule);
                setUnsavedChanges(true);
              }}
              className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
            >
              <span className="text-sm font-medium" style={{ color: '#0F172A' }}>
                Open All Days
              </span>
              <ChevronRight size={18} style={{ color: '#64748B' }} />
            </button>
            <button
              onClick={() => {
                const newSchedule = { ...schedule };
                DAYS.forEach((day) => {
                  newSchedule[day] = { ...newSchedule[day], isOpen: false };
                });
                setSchedule(newSchedule);
                setUnsavedChanges(true);
              }}
              className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
            >
              <span className="text-sm font-medium" style={{ color: '#0F172A' }}>
                Close All Days
              </span>
              <ChevronRight size={18} style={{ color: '#64748B' }} />
            </button>
            <button
              onClick={() => {
                const newSchedule = { ...initialHours };
                setSchedule(newSchedule);
                setUnsavedChanges(true);
              }}
              className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
            >
              <span className="text-sm font-medium" style={{ color: '#0F172A' }}>
                Reset to Default
              </span>
              <ChevronRight size={18} style={{ color: '#64748B' }} />
            </button>
          </div>
        </div>

        {/* Staff Schedules Link */}
        <button
          onClick={() => navigate(`/business/${businessId}/staff`)}
          className="w-full bg-light-card dark:bg-dark-card rounded-xl p-5 shadow-sm flex items-center justify-between hover:shadow-md transition-shadow"
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'rgba(209,160,47,0.12)' }}
            >
              <Users size={20} style={{ color: '#d1a02f' }} />
            </div>
            <div className="text-left">
              <p className="font-semibold" style={{ color: '#0F172A' }}>Staff Schedules</p>
              <p className="text-sm" style={{ color: '#64748B' }}>Manage individual staff availability</p>
            </div>
          </div>
          <ChevronRight size={20} style={{ color: '#64748B' }} />
        </button>

        {/* Save Button */}
        <button
          onClick={handleSave}
          disabled={saving || !unsavedChanges}
          className="w-full flex items-center justify-center gap-2 py-4 rounded-xl font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: '#4F46E5' }}
        >
          {saving ? (
            <Loader2 size={20} className="animate-spin" />
          ) : (
            <>
              <Save size={20} />
              Save Business Hours
            </>
          )}
        </button>
      </div>

      {/* Save Confirmation Toast */}
      {showSaveConfirm && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50">
          <div
            className="flex items-center gap-3 px-6 py-4 rounded-xl shadow-lg"
            style={{ backgroundColor: '#27ae60', color: '#fff' }}
          >
            <Check size={20} />
            <span className="font-semibold">Business hours saved successfully!</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessHoursPage;